/**
 * Hook for managing the sample data banner visibility and actions
 *
 * Shows a dismissible banner for teams with sample data that:
 * - Is visible for max 48 hours after sample data was seeded
 * - Disappears when user deletes sample data or dismisses the banner
 */

import { useState, useCallback, useMemo } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useTeam } from '@/contexts/TeamContext';
import { deleteSampleData } from '@/services/sampleData.service';
import { logger } from '@/lib/logger';

// FIX M2: Extended from 48 hours to 7 days for better user experience
const BANNER_VISIBILITY_HOURS = 168; // 7 days

interface UseSampleDataBannerReturn {
  /** Whether the banner should be visible */
  isVisible: boolean;
  /** Whether a delete operation is in progress */
  isDeleting: boolean;
  /** Dismiss the banner (sets sampleDataBannerDismissedAt) */
  dismissBanner: () => Promise<void>;
  /** Delete all sample data for the team */
  deleteAllSampleData: () => Promise<boolean>;
}

export function useSampleDataBanner(): UseSampleDataBannerReturn {
  const { team, activeTeamId } = useTeam();
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculate if banner should be visible
  const isVisible = useMemo(() => {
    if (!team || !activeTeamId) return false;

    // Must have sample data seeded
    if (!team.sampleDataSeededAt) return false;

    // Must not have been deleted
    if (team.sampleDataDeletedAt) return false;

    // FIX L4: Check if dismissal happened BEFORE current seeding
    // If user dismissed banner for a previous seeding, but re-seeded later,
    // the banner should show again for the new seeding
    if (team.sampleDataBannerDismissedAt) {
      const dismissedAt = team.sampleDataBannerDismissedAt;
      const seededAt = team.sampleDataSeededAt;
      // If dismissed after current seeding, don't show banner
      if (dismissedAt.getTime() > seededAt.getTime()) {
        return false;
      }
      // Otherwise, dismissal was for a previous seeding - show banner
    }

    // Must be within visibility window of seeding
    const seededAt = team.sampleDataSeededAt;
    const now = new Date();
    const hoursSinceSeeding = (now.getTime() - seededAt.getTime()) / (1000 * 60 * 60);

    if (hoursSinceSeeding > BANNER_VISIBILITY_HOURS) return false;

    // Also check hasSampleData flag as a safeguard
    if (!team.hasSampleData) return false;

    return true;
  }, [team, activeTeamId]);

  // Dismiss the banner
  const dismissBanner = useCallback(async () => {
    if (!activeTeamId) {
      logger.warn('[SampleDataBanner] Cannot dismiss: no active team');
      return;
    }

    try {
      const teamRef = doc(db, 'teams', activeTeamId);
      await updateDoc(teamRef, {
        sampleDataBannerDismissedAt: serverTimestamp(),
      });
      logger.info('[SampleDataBanner] Banner dismissed for team:', activeTeamId);
    } catch (error) {
      logger.error('[SampleDataBanner] Failed to dismiss banner:', error);
      throw error;
    }
  }, [activeTeamId]);

  // Delete all sample data
  const deleteAllSampleData = useCallback(async (): Promise<boolean> => {
    if (!activeTeamId) {
      logger.warn('[SampleDataBanner] Cannot delete: no active team');
      return false;
    }

    setIsDeleting(true);

    try {
      await deleteSampleData(activeTeamId);
      logger.info('[SampleDataBanner] Sample data deleted for team:', activeTeamId);
      return true;
    } catch (error) {
      logger.error('[SampleDataBanner] Failed to delete sample data:', error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [activeTeamId]);

  return {
    isVisible,
    isDeleting,
    dismissBanner,
    deleteAllSampleData,
  };
}
